// Pages/HistoryPage.tsx
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { GradeHistoryItem } from "../types/grading";
import {
  getOrCreateUserId,
  readHistory,
  writeLastResult,
} from "../utils/storage";
import { getAllCardHistory } from "../utils/API";

// API function to fetch history from backend
const fetchHistoryFromAPI = async (userId: string): Promise<any[]> => {
  try {
    const userId = localStorage.getItem("userId");
    const response = await getAllCardHistory(userId);
    console.log("API Response:", response);

    if (
      response.success &&
      response.history &&
      Array.isArray(response.history)
    ) {
      // Return the full data without transformation
      return response.history;
    }
    return [];
  } catch (error) {
    console.error("Failed to fetch history:", error);
    throw error;
  }
};

// Helper function to get recommendation styles
const getRecommendationStyles = (recommendation: string) => {
  switch (recommendation) {
    case "Hold":
      return "bg-green-100 text-green-800";
    case "Buy":
      return "bg-blue-100 text-blue-800";
    case "Sell":
      return "bg-amber-100 text-amber-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

export default function HistoryPage() {
  const navigate = useNavigate();
  const [remoteItems, setRemoteItems] = useState<any[] | null>(null);
  const [remoteError, setRemoteError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isLoading, setIsLoading] = useState(true);

  const localItems = useMemo(() => readHistory(), []);

  // Transform local items to match the expected display format
  const localDisplayItems = useMemo(() => {
    return localItems.map((item: any) => ({
      id: item.id,
      image_url: item.image_url,
      ai_grade: item.ai_grade,
      ai_reason: item.ai_reason,
      market_price: item.market_price,
      recommendation: item.recommendation,
      created_at: item.created_at,
      fullData: item, // Store the complete item
    }));
  }, [localItems]);

  // Transform remote items for display
  const displayItems = useMemo(() => {
    if (!remoteItems || remoteItems.length === 0) return localDisplayItems;

    return remoteItems.map((item: any) => ({
      id: item._id || item.id,
      image_url: item.image_url,
      ai_grade: item.analysis?.grade?.score || "N/A",
      ai_reason: item.analysis?.recommendation?.reason || "No reason provided",
      market_price: item.analysis?.market_price?.graded_price || "N/A",
      recommendation: item.analysis?.recommendation?.action || "Hold",
      created_at: item.created_at,
      fullData: item, // Store the complete API response
    }));
  }, [remoteItems, localDisplayItems]);

  // Filter items based on search term
  const filteredItems = useMemo(() => {
    if (!searchTerm) return displayItems;
    return displayItems.filter(
      (item) =>
        item.ai_grade.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.recommendation.toLowerCase().includes(searchTerm.toLowerCase()) ||
        item.ai_reason.toLowerCase().includes(searchTerm.toLowerCase()),
    );
  }, [displayItems, searchTerm]);

  // Fetch history from API
  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        setIsLoading(true);
        const userId = getOrCreateUserId();
        const data = await fetchHistoryFromAPI(userId);
        if (cancelled) return;

        if (data && data.length > 0) {
          setRemoteItems(data);
        } else {
          setRemoteItems([]);
        }
        setRemoteError(null);
      } catch (e: any) {
        if (cancelled) return;
        setRemoteError(
          e?.message ||
            "Failed to load history from server. Showing local history.",
        );
        console.error("API Error:", e);
      } finally {
        setIsLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  // Stats calculation
  const stats = useMemo(() => {
    if (filteredItems.length === 0) return null;

    const totalGrades = filteredItems.length;
    const averageGrade = Math.round(
      filteredItems.reduce((acc, item) => {
        const grade = parseFloat(item.ai_grade);
        return acc + (isNaN(grade) ? 0 : grade);
      }, 0) / totalGrades,
    );
    const holdCount = filteredItems.filter(
      (item) => item.recommendation === "Hold",
    ).length;
    const buyCount = filteredItems.filter(
      (item) => item.recommendation === "Buy",
    ).length;
    const sellCount = filteredItems.filter(
      (item) => item.recommendation === "Sell",
    ).length;

    return { totalGrades, averageGrade, holdCount, buyCount, sellCount };
  }, [filteredItems]);

  const handleItemClick = (item: any) => {
    // Create a GradeHistoryItem from the full data
    const resultItem: GradeHistoryItem = {
      id: item.id,
      user_id: item.fullData?.user_id || getOrCreateUserId(),
      image_url: item.image_url,
      defects_json:
        item.fullData?.analysis || item.fullData?.defects_json || {},
      ai_grade: item.ai_grade,
      ai_reason: item.ai_reason,
      market_price: item.market_price,
      recommendation: item.recommendation,
      created_at: item.created_at,
    };

    writeLastResult(resultItem);
    navigate("/result", { state: { item: resultItem } });
  };

  return (
    <div className="min-h-screen w-full overflow-x-hidden bg-slate-50 py-10">
      {/* Decorative background */}
      <div className="pointer-events-none fixed inset-0 overflow-hidden">
        <div className="absolute -top-24 -left-24 h-72 w-72 rounded-full bg-gradient-to-br from-blue-200/60 to-purple-200/60 blur-3xl" />
        <div className="absolute -bottom-24 -right-24 h-72 w-72 rounded-full bg-gradient-to-br from-indigo-200/60 to-sky-200/60 blur-3xl" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_1px_1px,rgba(15,23,42,0.06)_1px,transparent_0)] [background-size:22px_22px]" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header Section */}
        <div className="mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h1 className="text-3xl font-bold text-slate-900 mb-1">
                History
              </h1>
              <p className="text-sm text-slate-600">
                Past graded cards from your account
              </p>
            </div>
            <Link
              to="/"
              className="no-underline inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-emerald-500 hover:to-emerald-700 transition-all duration-200 shadow-sm hover:shadow-md gap-2"
            >
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Grade a card
            </Link>
          </div>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-4 border-emerald-500 border-t-transparent mb-4"></div>
            <p className="text-slate-600">Loading your history...</p>
          </div>
        )}

        {/* History List */}
        {!isLoading && filteredItems.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
            <div className="w-20 h-20 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-10 h-10 text-slate-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                />
              </svg>
            </div>
            <h3 className="text-lg font-semibold text-slate-900 mb-2">
              {searchTerm ? "No matching results" : "No graded cards yet"}
            </h3>
            <p className="text-sm text-slate-600 mb-6">
              {searchTerm
                ? "Try adjusting your search terms"
                : "Start by uploading a card image to get your first grade"}
            </p>
            {!searchTerm && (
              <Link
                to="/"
                className="inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 text-white text-sm font-medium rounded-lg hover:from-emerald-500 hover:to-emerald-700 transition-all duration-200 no-underline"
              >
                Upload a card
              </Link>
            )}
          </div>
        ) : (
          !isLoading && (
            <div className="grid grid-cols-1 gap-4">
              {filteredItems.map((it, index) => (
                <button
                  key={it.id}
                  onClick={() => handleItemClick(it)}
                  className="group w-full bg-white rounded-2xl border border-slate-200 shadow-sm hover:shadow-md transition-all duration-300 hover:-translate-y-0.5 text-left overflow-hidden"
                  style={{
                    animation: `fadeInUp 0.4s ease-out ${index * 0.05}s both`,
                  }}
                >
                  <div className="flex p-6 gap-6">
                    {/* Thumbnail Section */}
                    <div className="flex-shrink-0">
                      <div className="w-32 h-32 rounded-xl overflow-hidden border border-slate-200 bg-slate-50">
                        <img
                          src={it.image_url}
                          alt="Card thumbnail"
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          onError={(e) => {
                            (e.target as HTMLImageElement).src =
                              "/placeholder-image.png";
                          }}
                        />
                      </div>
                    </div>

                    {/* Content Section */}
                    <div className="flex-1 min-w-0">
                      {/* Grade and Price Row */}
                      <div className="flex items-center justify-between gap-4 flex-wrap mb-3">
                        <div className="flex items-center gap-3">
                          <div className="text-2xl font-bold bg-gradient-to-r from-emerald-400 via-emerald-500 to-emerald-600 bg-clip-text text-transparent">
                            {it.ai_grade}
                          </div>
                          <div
                            className={`px-2.5 py-1 rounded-full text-xs font-medium ${getRecommendationStyles(
                              it.recommendation,
                            )}`}
                          >
                            {it.recommendation}
                          </div>
                        </div>
                        <div className="text-xl font-bold text-slate-900">
                          {it.market_price}
                        </div>
                      </div>

                      {/* Reason */}
                      <p className="text-sm text-slate-600 leading-relaxed line-clamp-2 mb-3">
                        {it.ai_reason}
                      </p>

                      {/* Date */}
                      <div className="flex items-center gap-2 text-xs text-slate-500">
                        <svg
                          className="w-3.5 h-3.5"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <span>{new Date(it.created_at).toLocaleString()}</span>
                      </div>
                    </div>

                    {/* Arrow Icon */}
                    <div className="flex-shrink-0 self-center text-slate-400 group-hover:text-emerald-600 transition-colors">
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M9 5l7 7-7 7"
                        />
                      </svg>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )
        )}
      </div>
    </div>
  );
}
